const ALLOWED_ELEMENTS = [
  'a',
  'b',
  'br',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'i',
  'img',
  'li',
  'ol',
  'p',
  'span',
  'strong',
  'u',
  'ul'
];

const ALLOWED_ATTRS: Record<string, string[]> = {
  a: ['href', 'title', 'target', 'rel'],
  img: ['src', 'alt', 'width', 'height']
};

const SAFE_URL_PATTERN = /^(?:https?:\/\/|mailto:|\/)/i;

function isSafeUrl(value: string): boolean {
  return SAFE_URL_PATTERN.test(value.trim());
}

// The Sanitizer API spec uses Element.setHTML(html, { sanitizer }) rather than
function hasSetHTMLSanitizer(): boolean {
  return typeof globalThis.Sanitizer !== 'undefined' && typeof (HTMLElement.prototype as any).setHTML === 'function';
}

/**
 * Strips disallowed HTML tags and attributes to prevent stored XSS.
 * Uses the native Sanitizer API (Element.setHTML) when available, with a DOMParser-based fallback.
 */
export function sanitizeHtml(html: string): string {
  if (hasSetHTMLSanitizer()) {
    return sanitizeWithApi(html);
  }
  return sanitizeWithDomParser(html);
}

// --- Native Sanitizer API path (via Element.setHTML) ---

function sanitizeWithApi(html: string): string {
  // Config shape per current spec: https://wicg.github.io/sanitizer-api/#sanitizer
  // Per-element scoping is handled by postProcessNode below.
  const sanitizer = new globalThis.Sanitizer({
    elements: ALLOWED_ELEMENTS,
    attributes: [...new Set(Object.values(ALLOWED_ATTRS).flat())]
  });

  const container = document.createElement('div');
  // setHTML parses + sanitizes in one step
  (container as HTMLDivElement & { setHTML: (html: string, options: { sanitizer: Sanitizer }) => void }).setHTML(html, {
    sanitizer
  });

  postProcessNode(container);
  return container.innerHTML;
}

// --- DOMParser fallback ---

const ALLOWED_TAGS_SET = new Set(ALLOWED_ELEMENTS);
const ALLOWED_ATTRS_SET: Record<string, Set<string>> = Object.fromEntries(
  Object.entries(ALLOWED_ATTRS).map(([tag, attrs]) => [tag, new Set(attrs)])
);

function sanitizeWithDomParser(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  sanitizeNode(doc.body);
  postProcessNode(doc.body);
  return doc.body.innerHTML;
}

function sanitizeNode(node: Node): void {
  const children = Array.from(node.childNodes);

  for (const child of children) {
    if (child.nodeType === Node.TEXT_NODE) {
      continue;
    }

    if (child.nodeType !== Node.ELEMENT_NODE) {
      child.remove();
      continue;
    }

    const el = child as Element;
    const tag = el.tagName.toLowerCase();

    if (!ALLOWED_TAGS_SET.has(tag)) {
      const text = document.createTextNode(el.textContent ?? '');
      node.replaceChild(text, el);
      continue;
    }

    const allowedForTag = ALLOWED_ATTRS_SET[tag];
    for (const attr of Array.from(el.attributes)) {
      if (!allowedForTag?.has(attr.name)) {
        el.removeAttribute(attr.name);
      }
    }

    sanitizeNode(el);
  }
}

// --- Shared post-processing (URL validation + rel on links) ---

function postProcessNode(node: Node): void {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as Element;
    if (el.hasAttribute('href') && !isSafeUrl(el.getAttribute('href')!)) {
      el.removeAttribute('href');
    }
    if (el.hasAttribute('src') && !isSafeUrl(el.getAttribute('src')!)) {
      el.removeAttribute('src');
    }
    if (el.tagName.toLowerCase() === 'a') {
      el.setAttribute('rel', 'noopener noreferrer');
    }
  }
  for (const child of Array.from(node.childNodes)) {
    postProcessNode(child);
  }
}
